import React, { useState } from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { fmtCount, fmtDate } from "../format";
import useRole from "../hooks/useRole";
import CrmCard from "../ui/CrmCard";
import CrmButton from "../ui/CrmButton";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Active promotions" — item, ends on, views, clicks, purchases and "Stop early"
 * (owner; confirmation; no refund in v1). Rendered only when there is at least one.
 */
export default function ActivePromotionsTable({ rows, onChanged }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { can } = useRole();
  const [stopping, setStopping] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!rows || rows.length === 0) return null;

  const stop = async () => {
    setBusy(true);
    try {
      await crmApi.stopPromotion(stopping.id);
      toast.success(t("perf.promoStopped"));
      setStopping(null);
      onChanged?.();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmCard data-testid={CRM.perfPromos}>
      <h3 className="mb-2 font-display text-xl text-eh-ink">{t("perf.promosTitle")}</h3>
      <div className="overflow-x-auto" data-lenis-prevent>
        <table className="w-full min-w-[520px] text-sm text-eh-ink">
          <thead>
            <tr className="border-b border-eh-border-soft text-left text-xs uppercase tracking-wider text-eh-muted">
              <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colItem")}</th>
              <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colEndsOn")}</th>
              <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colViews")}</th>
              <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colClicks")}</th>
              <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colPurchases")}</th>
              <th scope="col" className="py-2 px-2"><span className="sr-only">{t("perf.colActions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-eh-border-soft">
                <td className="py-3 px-2">{p.item?.name}</td>
                <td className="py-3 px-2">{p.ends_at ? fmtDate(p.ends_at, lang) : "—"}</td>
                <td className="py-3 px-2">{fmtCount(p.stats?.views, lang)}</td>
                <td className="py-3 px-2">{fmtCount(p.stats?.clicks, lang)}</td>
                <td className="py-3 px-2">{fmtCount(p.stats?.purchases, lang)}</td>
                <td className="py-3 px-2 text-right">
                  {can("promoteBilling") && (
                    <CrmButton variant="link" size="sm" onClick={() => setStopping(p)} data-testid={CRM.perfPromoStop(p.id)}>
                      {t("perf.stopEarly")}
                    </CrmButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CrmAlertDialog
        open={!!stopping}
        onOpenChange={(o) => { if (!o) setStopping(null); }}
        title={t("perf.stopTitle")}
        description={t("perf.stopBody", { item: stopping?.item?.name || "" })}
        confirmLabel={t("perf.stopConfirm")}
        tone="danger"
        busy={busy}
        onConfirm={stop}
        confirmTestId={CRM.perfPromoStopConfirm}
      />
    </CrmCard>
  );
}
